import React, { useContext, useEffect, useRef } from 'react';
import GlobalContext from '../context/GlobalContext';
import { AuthContext } from 'react-oauth2-code-pkce';
import EventsService from '../services/events-service';

export default function ContextMenu() {
  const {
    contextMenu,
    setContextMenu,
    copiedEvent,
    setCopiedEvent,
    filteredEvents,
    fetchEvents,
    isReadonly, // Get readonly status from context
  } = useContext(GlobalContext);
  const { token, tokenData } = useContext(AuthContext);
  const menuRef = useRef(null);

  // Close context menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setContextMenu({ show: false, x: 0, y: 0, type: '', event: null, day: null });
      }
    }

    if (contextMenu.show) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu.show, setContextMenu]);

  // Close context menu on escape key
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setContextMenu({ show: false, x: 0, y: 0, type: '', event: null, day: null });
      }
    }

    if (contextMenu.show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [contextMenu.show, setContextMenu]);

  // Helper function to check if two events overlap
  function isOverlap(eventA, eventB) {
    if (!eventA.startTime || !eventA.endTime || !eventB.startTime || !eventB.endTime) return false;
    const [aStartH, aStartM] = eventA.startTime.split(":").map(Number);
    const [aEndH, aEndM] = eventA.endTime.split(":").map(Number);
    const [bStartH, bStartM] = eventB.startTime.split(":").map(Number);
    const [bEndH, bEndM] = eventB.endTime.split(":").map(Number);
    const aStart = aStartH * 60 + aStartM;
    const aEnd = aEndH * 60 + aEndM;
    const bStart = bStartH * 60 + bStartM;
    const bEnd = bEndH * 60 + bEndM;
    return aStart < bEnd && bStart < aEnd;
  }

  const handleCopy = () => {
    if (contextMenu.event) {
      // Copy the event, removing any projection-specific properties
      const eventToCopy = {
        ...contextMenu.event,
        projection: false, // Always copy as a standalone event
        recurrenceType: 'NONE', // Remove recurrence when copying
        recurrenceInterval: null,
        recurrenceEndDate: null,
      };
      setCopiedEvent(eventToCopy);
      
      // Show a brief feedback message
      const originalTitle = document.title;
      document.title = '📋 Event gekopieerd!';
      setTimeout(() => {
        document.title = originalTitle;
      }, 1500);
    }
    setContextMenu({ show: false, x: 0, y: 0, type: '', event: null, day: null });
  };

  const handlePaste = async () => {
    if (!copiedEvent || !contextMenu.day) return;

    try {
      // Create new event data for the target day
      const newEvent = {
        ...copiedEvent,
        id: undefined, // Let server assign new ID
        day: contextMenu.day,
      };

      // Check for conflicts with existing events on this day
      const dayEvents = filteredEvents.filter(evt => evt.day === contextMenu.day);
      const hasConflict = dayEvents.some(evt => isOverlap(evt, newEvent));
      
      if (hasConflict) {
        alert('Kan event niet plakken: tijd overlapt met een bestaande afspraak!');
        setContextMenu({ show: false, x: 0, y: 0, type: '', event: null, day: null });
        return;
      }

      // Save the new event
      await EventsService.saveEvent(newEvent, token);
      
      // Show success feedback
      const originalTitle = document.title;
      document.title = '✅ Event geplakt!';
      setTimeout(() => {
        document.title = originalTitle;
      }, 1500);
      
      // Refresh events
      if (typeof fetchEvents === 'function') {
        setTimeout(() => fetchEvents(), 0);
      }

    } catch (err) {
      alert('Fout bij plakken van event: ' + (err.message || err));
    }

    setContextMenu({ show: false, x: 0, y: 0, type: '', event: null, day: null });
  };

  const canPaste = () => {
    if (!copiedEvent || !contextMenu.day) return false;
    
    // Check if pasting would create a conflict
    const dayEvents = filteredEvents.filter(evt => evt.day === contextMenu.day);
    const newEvent = { ...copiedEvent, day: contextMenu.day };
    return !dayEvents.some(evt => isOverlap(evt, newEvent));
  };

  if (!contextMenu.show || isReadonly) return null;

  // Adjust position to prevent menu from going off-screen
  const menuWidth = 144; // min-w-36 = 144px
  const menuHeight = contextMenu.type === 'event' ? 48 : (copiedEvent ? 48 : 48);
  const adjustedX = Math.min(contextMenu.x, window.innerWidth - menuWidth - 10);
  const adjustedY = Math.min(contextMenu.y, window.innerHeight - menuHeight - 10);

  return (
    <div
      ref={menuRef}
      className="fixed z-[60] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 min-w-36 text-sm text-gray-700 dark:text-gray-200"
      style={{
        left: adjustedX,
        top: adjustedY,
      }}
    >
      {contextMenu.type === 'event' && (
        <button
          onClick={handleCopy}
          className="w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
        >
          <span className="material-icons-outlined text-sm">content_copy</span>
          Kopiëren
        </button>
      )}
      
      {contextMenu.type === 'day' && copiedEvent && (
        <button
          onClick={handlePaste}
          disabled={!canPaste()}
          className={`w-full px-4 py-2 text-left transition-colors flex items-center gap-2 ${
            canPaste() 
              ? 'hover:bg-green-50' 
              : 'text-gray-400 cursor-not-allowed bg-gray-50'
          }`}
          title={!canPaste() ? 'Kan niet plakken: tijd overlapt met bestaande afspraak' : ''}
        >
          <span className={`material-icons-outlined text-sm ${!canPaste() ? 'text-gray-400' : ''}`}>
            content_paste
          </span>
          Plakken
        </button>
      )}
      
      {contextMenu.type === 'day' && !copiedEvent && (
        <div className="px-4 py-2 text-gray-400 text-sm">
          Geen event gekopieerd
        </div>
      )}
    </div>
  );
}
